// frontend/app/components/lessonPlans/StartLessonCard.js
//
// "Start a new lesson": the teacher library's way to author rather than find.
// Collapsed by default to one quiet toggle row (styled like "Or browse by
// subject" in LessonBrowseTree); pressing it opens two equal tiles — build from
// scratch, or upload a lesson JSON file — under the finder. An upload error
// opens it, so a failure is never hidden behind a closed card. Tiles stack when the card is narrow. The upload tile is web only
// (a file picker needs a DOM input); on native only "Create from scratch"
// shows, full width.
//
// Upload logic lives in useLessonUpload; errors render inside the card, below
// the tiles, in the same dismissable panel the admin library uses.

import React, { useEffect, useState } from "react";
import {
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { getLessonColors, tint } from "./lessonPlanTheme";
import useLessonUpload from "./useLessonUpload";
import { HiddenLessonFileInput, LessonUploadErrorPanel } from "./LessonUploadParts";

// Below this card width the two tiles stack.
export const STACK_BELOW = 560;
const ICON_CIRCLE = 40;

const Tile = ({ icon, title, caption, colors, isDarkMode, testID, children }) => (
  <View
    testID={testID}
    style={[
      styles.tile,
      { borderColor: colors.border, backgroundColor: colors.subtleBg },
    ]}
  >
    <View
      style={[
        styles.iconCircle,
        { backgroundColor: tint(colors.accentPrimary, isDarkMode, 1.4) },
      ]}
    >
      <MaterialCommunityIcons name={icon} size={20} color={colors.accentPrimary} />
    </View>
    <Text style={[styles.tileTitle, { color: colors.textPrimary }]}>{title}</Text>
    <Text style={[styles.tileCaption, { color: colors.textMuted }]}>{caption}</Text>
    <View style={styles.tileActions}>{children}</View>
  </View>
);

const StartLessonCard = ({
  onCreateLesson,
  fetchWithAuth,
  onImported,
  isDarkMode = false,
  t = (s) => s,
  style,
}) => {
  const colors = getLessonColors(isDarkMode);
  const { width: windowWidth } = useWindowDimensions();
  const [measuredWidth, setMeasuredWidth] = useState(0);
  const [open, setOpen] = useState(false);
  const upload = useLessonUpload({ fetchWithAuth, onImported, t });
  const hasUploadError = upload.errors.length > 0;

  // Not persisted: a fresh visit starts collapsed. An error opens the card.
  useEffect(() => {
    if (hasUploadError) setOpen(true);
  }, [hasUploadError]);

  const showCreate = typeof onCreateLesson === "function";
  const showUpload =
    Platform.OS === "web" &&
    typeof onImported === "function" &&
    typeof fetchWithAuth === "function";
  if (!showCreate && !showUpload) return null;

  const stacked = (measuredWidth || windowWidth) < STACK_BELOW;

  const expanded = open || hasUploadError;

  return (
    <View
      testID="start-lesson-card"
      style={[styles.wrap, style]}
      onLayout={(event) => setMeasuredWidth(event.nativeEvent.layout.width)}
    >
      <TouchableOpacity
        testID="start-lesson-toggle"
        onPress={() => setOpen(!expanded)}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel={t("Start a new lesson")}
        style={styles.toggle}
      >
        <MaterialCommunityIcons name="plus" size={16} color={colors.textSecondary} />
        <Text style={[styles.toggleText, { color: colors.textSecondary }]}>
          {t("Start a new lesson")}
        </Text>
        <MaterialCommunityIcons
          name={expanded ? "chevron-up" : "chevron-down"}
          size={16}
          color={colors.textSecondary}
        />
      </TouchableOpacity>

      {expanded ? (
        <View
          style={[
            styles.card,
            {
              backgroundColor: colors.panelBg,
              borderColor: colors.border,
              shadowColor: colors.shadow,
              shadowOpacity: colors.shadowOpacity,
            },
          ]}
        >
          <View style={[styles.tiles, stacked && styles.tilesStacked]}>
            {showCreate ? (
              <Tile
                testID="start-lesson-create-tile"
                icon="pencil-outline"
                title={t("Create from scratch")}
                caption={t("Build it step by step in the editor.")}
                colors={colors}
                isDarkMode={isDarkMode}
              >
                <TouchableOpacity
                  testID="lesson-create-link"
                  onPress={onCreateLesson}
                  accessibilityRole="button"
                  accessibilityLabel={t("Create lesson")}
                  style={[styles.filledBtn, { backgroundColor: colors.accentPrimary }]}
                >
                  <Text style={styles.filledBtnText}>{t("Create lesson")}</Text>
                </TouchableOpacity>
              </Tile>
            ) : null}

            {showUpload ? (
              <Tile
                testID="start-lesson-upload-tile"
                icon="file-upload-outline"
                title={t("Upload a lesson file")}
                caption={t(".json file, up to 1 MB.")}
                colors={colors}
                isDarkMode={isDarkMode}
              >
                <TouchableOpacity
                  testID="lesson-upload-button"
                  onPress={upload.openPicker}
                  disabled={upload.busy}
                  accessibilityRole="button"
                  accessibilityLabel={t("Choose file")}
                  style={[
                    styles.outlinedBtn,
                    { borderColor: colors.accentPrimary },
                    upload.busy && styles.busy,
                  ]}
                >
                  <Text style={[styles.outlinedBtnText, { color: colors.accentPrimary }]}>
                    {upload.busy ? t("Uploading…") : t("Choose file")}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  testID="lesson-example-download"
                  onPress={upload.downloadExample}
                  disabled={upload.downloading}
                  accessibilityRole="button"
                  accessibilityLabel={t("Download example file")}
                  style={[styles.link, upload.downloading && styles.busy]}
                >
                  <MaterialCommunityIcons
                    name="download-outline"
                    size={14}
                    color={colors.accentPrimary}
                  />
                  <Text style={[styles.linkText, { color: colors.accentPrimary }]}>
                    {t("Download example file")}
                  </Text>
                </TouchableOpacity>
              </Tile>
            ) : null}
          </View>

          {showUpload ? (
            <>
              <HiddenLessonFileInput inputRef={upload.inputRef} onChange={upload.handleFile} />
              <LessonUploadErrorPanel
                errors={upload.errors}
                onDismiss={upload.clearErrors}
                colors={colors}
                t={t}
              />
            </>
          ) : null}
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: { marginTop: 24 },
  // Same quiet look as the "Or browse by subject" toggle: no border, no fill.
  toggle: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "center",
    gap: 5,
    paddingVertical: 6,
    paddingHorizontal: 2,
  },
  toggleText: { fontSize: 13, fontWeight: "600" },
  card: {
    marginTop: 8,
    borderWidth: 1,
    borderRadius: 16,
    padding: 20,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 12,
    elevation: 2,
  },
  tiles: { flexDirection: "row", gap: 16 },
  tilesStacked: { flexDirection: "column" },
  tile: {
    flex: 1,
    minWidth: 0,
    borderWidth: 1,
    borderRadius: 12,
    padding: 16,
  },
  iconCircle: {
    width: ICON_CIRCLE,
    height: ICON_CIRCLE,
    borderRadius: ICON_CIRCLE / 2,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 12,
  },
  tileTitle: { fontSize: 15, fontWeight: "700" },
  tileCaption: { fontSize: 13, lineHeight: 18, marginTop: 4 },
  tileActions: { marginTop: 14, alignItems: "flex-start", gap: 10 },
  filledBtn: { borderRadius: 8, paddingHorizontal: 16, paddingVertical: 9 },
  filledBtnText: { color: "#ffffff", fontSize: 13, fontWeight: "700" },
  outlinedBtn: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  outlinedBtnText: { fontSize: 13, fontWeight: "700" },
  link: { flexDirection: "row", alignItems: "center", gap: 4 },
  linkText: { fontSize: 12, fontWeight: "600" },
  busy: { opacity: 0.6 },
});

export default StartLessonCard;
